import { collection, query, where, getDocs, doc, updateDoc, increment } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Validates a coupon or promo code against the current cart total.
 * Supports percentage, fixed discount, expiry dates, usage limits, and min order thresholds.
 */
export async function validateCoupon(code, cartTotal) {
  if (!code?.trim()) return { valid: false, error: "Please enter a promo code" };
  const cleanCode = code.trim().toUpperCase();

  try {
    const snap = await getDocs(query(collection(db, "coupons"), where("code", "==", cleanCode)));
    if (snap.empty) {
      return { valid: false, error: `Promo code "${cleanCode}" does not exist` };
    }

    const coupon = { id: snap.docs[0].id, ...snap.docs[0].data() };

    // Active status check
    if (coupon.active === false) {
      return { valid: false, error: `Promo code "${cleanCode}" is currently inactive` };
    }

    // Expiry check
    if (coupon.expiresAt) {
      let expiryDate;
      if (typeof coupon.expiresAt?.toDate === "function") {
        expiryDate = coupon.expiresAt.toDate();
      } else if (typeof coupon.expiresAt === "string" || typeof coupon.expiresAt === "number") {
        expiryDate = new Date(coupon.expiresAt);
      }
      if (expiryDate && !isNaN(expiryDate.getTime()) && expiryDate < new Date()) {
        return { valid: false, error: `Promo code "${cleanCode}" has expired` };
      }
    }

    // Usage limit check
    if (coupon.usageLimit && Number(coupon.usageLimit) > 0) {
      const used = Number(coupon.usedCount || 0);
      if (used >= Number(coupon.usageLimit)) {
        return { valid: false, error: `Promo code "${cleanCode}" has reached its maximum usage limit` };
      }
    }

    // Minimum order check
    const minOrder = Number(coupon.minOrderAmount || 0);
    if (minOrder > 0 && cartTotal < minOrder) {
      return {
        valid: false,
        error: `Minimum order of ₦${minOrder.toLocaleString()} required for code "${cleanCode}" (current subtotal: ₦${Math.round(cartTotal).toLocaleString()})`
      };
    }

    // Calculate discount
    let discount = 0;
    const value = Number(coupon.value || 0);
    if (coupon.type === "percentage") {
      discount = Math.round((cartTotal * value) / 100);
      if (coupon.maxDiscount && Number(coupon.maxDiscount) > 0) {
        discount = Math.min(discount, Number(coupon.maxDiscount));
      }
    } else {
      // Fixed amount
      discount = Math.min(value, cartTotal);
    }

    return { valid: true, coupon, discount };
  } catch (err) {
    console.error("Error validating coupon:", err);
    return { valid: false, error: "Could not validate promo code. Please try again." };
  }
}

/**
 * Increments the redemption counter for a coupon.
 */
export async function redeemCoupon(couponId) {
  if (!couponId) return;
  try {
    await updateDoc(doc(db, "coupons", couponId), {
      usedCount: increment(1),
    });
  } catch (err) {
    console.warn(`Could not increment usedCount for coupon ${couponId}:`, err?.message);
  }
}

// Backwards-compatible alias
export const useCoupon = redeemCoupon;

